/**
 * Integrytas Group - Offline Data Sync (ODS)
 * Page: ODS.SYNC
 *
 * Configure sync-enabled entities and their conflict strategy. Attaching the
 * CDC trigger is a DBA action: SELECT ods_enable_sync('entity','pk_col','STRATEGY');
 * this page manages the registry + strategy for already-registered entities.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ODS.SYNC';

type Strategy = 'LAST_WRITE_WINS' | 'FIELD_MERGE' | 'USER_RECONCILE';
type Tiebreak = 'LATEST' | 'SERVER' | 'CLIENT';
interface EntityRow {
  ods_sync_entity_id: number; entity_name: string; pk_column: string;
  strategy: Strategy; lww_tiebreak: Tiebreak; is_active: 'Y' | 'N'; description: string | null;
}

const ENTITY_GRID_COLS: GridColumnDef[] = [
  { key: 'entity_name',  header: 'Entity',       defaultWidth: 220 },
  { key: 'pk_column',    header: 'PK',           defaultWidth: 140 },
  { key: 'strategy',     header: 'Strategy',     defaultWidth: 200 },
  { key: 'lww_tiebreak', header: 'LWW Tiebreak', defaultWidth: 160 },
  { key: 'is_active',    header: 'Active',       defaultWidth: 90  },
];

export interface OdsSyncManagerPageProps { session: AuthSession }

export function OdsSyncManagerPage({ session }: OdsSyncManagerPageProps) {
  return (
    <PersonalizationProvider pageCode="ODS.SYNC" session={session}>
      <OdsSyncManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function OdsSyncManagerPageInner({ session }: OdsSyncManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows] = useState<EntityRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newEntity, setNewEntity] = useState('');
  const [newPk, setNewPk] = useState('');
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the list's logical columns.
  const [facets, setFacets] = useState<{ strategy: string[]; tiebreak: string[]; active: string[] }>(
    { strategy: [], tiebreak: [], active: ['Y'] },
  );

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('ods_sync_entity')
        .select('ods_sync_entity_id, entity_name, pk_column, strategy, lww_tiebreak, is_active, description').order('entity_name');
      if (e) throw e;
      setRows((data ?? []) as EntityRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const save = async (r: EntityRow) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedUpdate(session, 'ods_sync_entity', 'ods_sync_entity_id', r.ods_sync_entity_id, {
        strategy: r.strategy, lww_tiebreak: r.lww_tiebreak, is_active: r.is_active,
      });
      if (e) throw e;
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const addRow = async () => {
    if (!newEntity.trim() || !newPk.trim()) { setError(t('ods_sync.err_entity_pk_required', 'Entity and PK column are required.')); return; }
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedInsert(session, 'ods_sync_entity', { entity_name: newEntity.trim(), pk_column: newPk.trim(), strategy: 'LAST_WRITE_WINS', is_active: 'N' });
      if (e) throw e;
      setNewEntity(''); setNewPk(''); await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const patch = (i: number, p: Partial<EntityRow>) => setRows(rs => rs.map((x, j) => j === i ? { ...x, ...p } : x));
  const detail = useExpandedRows<number>();

  // §17.B13 related-children (expand/contract). A sync entity's conflicts live on
  // a separate page and are not loaded per row here, so the panel is inert.
  // TODO: wire relations (surface ods_conflict rows for this entity once loaded).
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      strategy: uniq(rows.map(r => r.strategy)).map(v => ({ value: v, label: v })),
      tiebreak: uniq(rows.map(r => r.lww_tiebreak)).map(v => ({ value: v, label: v })),
      active: [{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.strategy.length + facets.tiebreak.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ strategy: [], tiebreak: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (r: EntityRow) =>
      (facets.strategy.length === 0 || facets.strategy.includes(r.strategy)) &&
      (facets.tiebreak.length === 0 || facets.tiebreak.includes(r.lww_tiebreak)) &&
      (facets.active.length   === 0 || facets.active.includes(r.is_active));
    return rows
      .filter(r => !q || r.entity_name.toLowerCase().includes(q) || r.pk_column.toLowerCase().includes(q))
      .filter(matchesFacets);
  }, [rows, filter, facets]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('ods_sync.heading', 'Offline Sync Entities')}
          description={t('ods_sync.list.description', 'Entities enabled for offline sync and the conflict strategy applied when their rows collide.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('ods_sync.refresh', 'Refresh')}
            </button>
          }
        />
        {error && (
          <ErrorBanner
            message={t('ods_sync.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('ods_sync.filter_placeholder', 'Filter entities…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('ods_sync.col.strategy', 'Strategy')} options={facetOptions.strategy} selected={facets.strategy} onChange={v => setFacets(f => ({ ...f, strategy: v }))} />
            <FacetFilter label={t('ods_sync.col.lww_tiebreak', 'LWW Tiebreak')} options={facetOptions.tiebreak} selected={facets.tiebreak} onChange={v => setFacets(f => ({ ...f, tiebreak: v }))} />
            <FacetFilter label={t('ods_sync.col.is_active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('ods_sync.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-end gap-2 border-b border-csm-line bg-white px-3 py-2">
            <input value={newEntity} onChange={e => setNewEntity(e.target.value)} placeholder={t('ods_sync.entity_placeholder', 'entity (table)')} className="h-8 w-48 rounded-csm-control border border-csm-border px-2 font-mono text-csm-sm" />
            <input value={newPk} onChange={e => setNewPk(e.target.value)} placeholder={t('ods_sync.pk_placeholder', 'pk column')} className="h-8 w-36 rounded-csm-control border border-csm-border px-2 font-mono text-csm-sm" />
            {canAdd && (<button onClick={() => void addRow()} className="h-8 rounded border border-sidebar bg-sidebar px-3 text-csm-sm text-white hover:bg-sidebar-alt">{t('ods_sync.register_entity', 'Register Entity')}</button>)}
            <span className="text-csm-2xs text-csm-watermark">{t('ods_sync.dba_hint_pre', 'then a DBA runs')} <span className="font-mono">SELECT ods_enable_sync('entity','pk','STRATEGY')</span> {t('ods_sync.dba_hint_post', 'to attach the CDC trigger.')}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={ENTITY_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('ods_sync.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('ods_sync.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('ods_sync.col_save', 'Save')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const i = rows.indexOf(r);
                  return (
                    <Fragment key={r.ods_sync_entity_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle">
                        <RelatedExpandButton open={detail.isOpen(r.ods_sync_entity_id)} hasChildren={hasRelated(r.ods_sync_entity_id)} onToggle={() => detail.toggle(r.ods_sync_entity_id)} expandLabel={t('ods_sync.related.expand', 'Show related records')} collapseLabel={t('ods_sync.related.collapse', 'Hide related')} tooltip={t('ods_sync.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'entity_name'  && <span className="font-mono text-csm-sm">{r.entity_name}</span>}
                          {col.key === 'pk_column'    && <span className="font-mono text-csm-xs">{r.pk_column}</span>}
                          {col.key === 'strategy' && (
                            <select value={r.strategy} onChange={e => patch(i, { strategy: e.target.value as Strategy })} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                              <option value="LAST_WRITE_WINS">LAST_WRITE_WINS</option><option value="FIELD_MERGE">FIELD_MERGE</option><option value="USER_RECONCILE">USER_RECONCILE</option>
                            </select>
                          )}
                          {col.key === 'lww_tiebreak' && (
                            <select value={r.lww_tiebreak} onChange={e => patch(i, { lww_tiebreak: e.target.value as Tiebreak })} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                              <option value="LATEST">LATEST</option><option value="SERVER">SERVER</option><option value="CLIENT">CLIENT</option>
                            </select>
                          )}
                          {col.key === 'is_active' && (
                            <input type="checkbox" checked={r.is_active === 'Y'} onChange={e => patch(i, { is_active: e.target.checked ? 'Y' : 'N' })} className="h-4 w-4 accent-gray-700" />
                          )}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                        {canChange && (<button onClick={() => void save(r)} className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover">{t('ods_sync.save', 'Save')}</button>)}
                      </td>
                    </tr>
                    {detail.isOpen(r.ods_sync_entity_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3">
                          <RelatedChildrenPanel groups={buildRelatedGroups(r.ods_sync_entity_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('ods_sync.empty', 'No sync entities.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
            )}
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
